import { BusinessIcon } from "assets/icons";
import { If } from "components";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useBoolean } from "hooks";
import { t } from "i18next";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Pagination, SearchInput } from "ui";

import {
  ProductsFilter,
  ProductsTable,
  DeleteProductModal,
  MerchantPriceModal
} from "../../components";
import { useProductsList } from "../../hooks";

import styles from "./Products.module.scss";

const Products = () => {
  const navigate = useNavigate();
  const {
    products,
    totalItems,
    searchValue,
    page,
    sorting,
    isLoading,
    setSorting,
    setPage,
    setSearchValue,
    refetchProducts,

    categories,
    filterMeta,
    isCategoriesLoading,
    isFilterMetaLoading
  } = useProductsList();

  const { user } = useProfile({ enabled: false });
  const { canAddProduct } = useMemo(() => {
    return {
      canAddProduct:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.CreateProducts)
    };
  }, [user]);

  const navigateToCategory = () => {
    navigate("/products/category");
  };
  const [deletingProductId, setDeletingProductId] = useState<string | null>(
    null
  );

  const {
    value: isMerchantPriceModalOpen,
    setTrue: openMerchantPriceModal,
    setFalse: closeMerchantPriceModal
  } = useBoolean(false);

  return (
    <ContentLayout
      title={t("Products")}
      counter={totalItems}
      action={
        <Button onClick={navigateToCategory} disabled={!canAddProduct}>
          {t("Add Product")}
        </Button>
      }
    >
      <div className={styles.productsContent}>
        <ProductsFilter
          categories={categories}
          filterMeta={filterMeta}
          isFilterMetaLoading={isFilterMetaLoading}
          isCategoriesLoading={isCategoriesLoading}
        />

        <BoxShadowLayout className={styles.productsContent_rightBlock}>
          <div className={styles.productsContent_tableHeaderControls}>
            <SearchInput
              value={searchValue}
              onChange={setSearchValue}
              placeholder={t(`${t("Search")}...`)}
              data-testid="products--search"
            />
            <Button
              variant={"default"}
              title={t("Set Merchant Price")}
              icon={<BusinessIcon />}
              onClick={openMerchantPriceModal}
              data-testid="products--merchant-price"
            />
          </div>

          <div className={styles.productsContent_tableContainer}>
            <ProductsTable
              products={products ?? []}
              sorting={sorting}
              setSorting={setSorting}
              isLoading={isLoading}
              onDeleteProduct={setDeletingProductId}
            />
          </div>
          <Pagination
            currentPage={page}
            onChange={setPage}
            perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
            totalItems={totalItems}
          />
        </BoxShadowLayout>
      </div>

      <If condition={deletingProductId}>
        <DeleteProductModal
          id={String(deletingProductId)}
          isOpen={Boolean(deletingProductId)}
          onClose={() => setDeletingProductId(null)}
          onDeleteSuccess={refetchProducts}
        />
      </If>

      <If condition={isMerchantPriceModalOpen}>
        <MerchantPriceModal
          isOpen={isMerchantPriceModalOpen}
          onClose={closeMerchantPriceModal}
        />
      </If>
    </ContentLayout>
  );
};

export default Products;
